import { getUser } from '../auth/authService';

// The shared provider dictionary (management.NexusIntegrationProvider). Reads
// and inserts only — nothing here can rename or remove a row, since other
// flows across the platform read this same table.
const BASE = `${process.env.REACT_APP_INTEGRATION_MICROSERVICE_URL ?? ''}`;

async function authHeaders() {
    const user = await getUser();
    if (!user?.access_token) throw new Error('Not authenticated');
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${user.access_token}`,
    };
}

async function parseError(res) {
    try {
        const body = await res.json();
        return body?.detail ?? `HTTP ${res.status}`;
    } catch {
        const text = await res.text().catch(() => '');
        if (!text || text.trimStart().startsWith('<')) return `HTTP ${res.status}`;
        return text.slice(0, 300);
    }
}

async function request(path, options = {}) {
    const headers = { ...(await authHeaders()), ...(options.headers || {}) };
    const res = await fetch(`${BASE}${path}`, { ...options, headers });
    if (!res.ok) {
        const err = new Error(await parseError(res));
        err.status = res.status;
        throw err;
    }
    return res.json();
}

/** The full picklist, `{id, name}`, for the "add provider" typeahead. */
export const listProviders = async () => (await request('/integration-providers')).providers ?? [];

/**
 * Every provider in the dictionary, `{id, name, type, clientCount,
 * connectedCount, lastUpdated}` — the landing grid. `type` is whatever
 * data-source type shows up most often across the clients that have this
 * provider wired up (the dictionary itself doesn't store one).
 */
export const listProvidersOverview = async () => (await request('/integration-providers/overview')).providers ?? [];

/**
 * Insert a provider if the name isn't already in the dictionary — the
 * backend returns the existing row instead of duplicating one. Used only when
 * a client's provider genuinely isn't in the picklist yet.
 */
export const createProvider = async (name, createdBy) =>
    request('/integration-providers', {
        method: 'POST',
        body: JSON.stringify({ name, created_by: createdBy }),
    });

/**
 * One provider's own write-up — Category + What it is + the "Data we pull"
 * dataset list + the segment-drift-flag switch + Key Contacts + Notes/Links +
 * the Outreach template — shared across every client that has it (NOT
 * per-client): it describes the provider itself and how Nexus generally
 * works with it, not any one district's relationship to it.
 */
export const getProviderInfo = (providerId) =>
    request(`/integration-providers/${encodeURIComponent(providerId)}`);

/** Save a provider's write-up. 409s if `updatedOn` is stale. */
export const saveProviderInfo = (
    providerId,
    { category, whatItIs, datasets, segmentFlagsDisabled, contacts, notesHtml, links, outreachHtml },
    updatedBy, updatedOn,
) =>
    request(`/integration-providers/${encodeURIComponent(providerId)}`, {
        method: 'PUT',
        body: JSON.stringify({
            category: category || '', what_it_is: whatItIs || '', datasets: datasets || [],
            segment_flags_disabled: !!segmentFlagsDisabled,
            contacts: contacts || [], notes_html: notesHtml || '', links: links || [], outreach_html: outreachHtml || '',
            updated_by: updatedBy, updated_on: updatedOn ?? null,
        }),
    });

/**
 * Every client's live segment names for this provider, `{clientId: [name,...]}`
 * — fetched once when a provider's page opens, for the segment-drift flag on
 * each client row. Not per-client detail (the Schema tab already does that
 * lazily) — just names, in bulk.
 */
export const getProviderSegmentSummary = async (providerId) =>
    (await request(`/integration-providers/${encodeURIComponent(providerId)}/segment-summary`)).segmentsByClient ?? {};
